/*
 * This file is part of the Det Norske Teatret Nettside 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useEffect, type ComponentProps } from 'react';
import { CookiesProvider, useCookies } from 'react-cookie';
import NodeTransition from 'components/NodeTransition/NodeTransition';
import ExitIcon from 'components/Icons/Exit/Exit';
import './CookieConsent.scss';

interface LinkProps extends ComponentProps<'a'> {
  text: string;
}

interface CookieConsentProps {
  text: string;
  link: LinkProps;
  close?: string;
  accept?: string;
}

/**
 * This is the CookieConsent component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 */
function CookieConsentContent({
  text,
  link: { text: linkText, ...link },
  close = 'Close',
  accept = 'Accept',
}: CookieConsentProps) {
  const [{ dntCookieConsent }, setCookie] = useCookies(['dntCookieConsent']);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // react-cookie 8 parses the stored 'true' into a boolean.
    setVisible(String(dntCookieConsent) !== 'true');
  }, [dntCookieConsent]);

  function onAccept() {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);

    setCookie('dntCookieConsent', true, {
      path: '/',
      expires,
    });
  }

  return (
    <NodeTransition in={visible} timeout={900} unmountOnExit>
      <aside className="cookie-consent">
        <div>
          <div className="close">
            <button onClick={onAccept}>
              <ExitIcon title={close} />
            </button>
          </div>
          <p>
            {text}&nbsp;<a {...link}>{linkText}</a>
          </p>
          <button className="accept" onClick={onAccept}>
            {accept}
          </button>
        </div>
      </aside>
    </NodeTransition>
  );
}

// react-cookie 8 requires a provider; this is the only component using it.
function CookieConsent(props: CookieConsentProps) {
  return (
    <CookiesProvider>
      <CookieConsentContent {...props} />
    </CookiesProvider>
  );
}

export default CookieConsent;
